import { CollapsibleFilters } from "../common/CollapsibleFilters";
import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  FormControl,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import { useJobWorkspace } from "./JobWorkspace";

export function useEvidenceFilters<
  T extends {
    id: number;
    tags?: { name: string }[];
    uploaded_at?: string;
    created_at?: string;
    description?: string;
    file_name?: string;
  },
>(records: T[]) {
  const [tag, setTag] = useState("");
  const [phase, setPhase] = useState("");
  const [from, setFrom] = useState("");
  const [through, setThrough] = useState("");
  const tags = Array.from(
    new Set(
      records.flatMap((record) => record.tags?.map((tag) => tag.name) || []),
    ),
  ).sort();
  const filtered = records.filter((record) => {
    const names = record.tags?.map((tag) => tag.name.toLowerCase()) || [];
    const date = (record.uploaded_at || record.created_at || "").slice(0, 10);
    return (
      (!tag || names.includes(tag.toLowerCase())) &&
      (!phase || names.includes(phase)) &&
      (!from || (date && date >= from)) &&
      (!through || (date && date <= through))
    );
  });
  const controls = (
    <CollapsibleFilters activeCount={[phase, tag, from, through].filter(Boolean).length}><Card variant="soft" sx={{ mb: 2 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2,1fr)",
            lg: "repeat(4,1fr)",
          },
          gap: 1,
        }}
      >
        <FormControl>
          <FormLabel>Phase tag</FormLabel>
          <Select value={phase} onChange={(_, v) => setPhase(v || "")}>
            <Option value="">All phases</Option>
            {["before", "during", "after", "coc", "supplement"].map((name) => (
              <Option key={name} value={name}>
                {name}
              </Option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Tag</FormLabel>
          <Select value={tag} onChange={(_, v) => setTag(v || "")}>
            <Option value="">All tags</Option>
            {tags.map((name) => (
              <Option key={name} value={name}>
                {name}
              </Option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Uploaded from</FormLabel>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </FormControl>
        <FormControl>
          <FormLabel>Uploaded through</FormLabel>
          <Input
            type="date"
            value={through}
            onChange={(e) => setThrough(e.target.value)}
          />
        </FormControl>
      </Box>
      <Typography level="body-xs">
        {filtered.length} of {records.length} files. Use the existing tag editor
        to assign before, during, after or report phases.
      </Typography>
      <Button
        size="sm"
        variant="plain"
        onClick={() => {
          setTag("");
          setPhase("");
          setFrom("");
          setThrough("");
        }}
      >
        Clear evidence filters
      </Button>
    </Card></CollapsibleFilters>

  );
  return { filtered, controls };
}
const name = (file: any) =>
  file.file_name || file.description || `File ${file.id}`;
export function PhotoComparisons({
  jobId,
  photos,
}: {
  jobId: string;
  photos: any[];
}) {
  const { data, command, error } = useJobWorkspace(jobId);
  const [before, setBefore] = useState<number | null>(null);
  const [after, setAfter] = useState<number | null>(null);
  const select = (
    label: string,
    value: number | null,
    set: (v: number | null) => void,
  ) => (
    <FormControl sx={{ flex: 1, minWidth: 140 }}>
      <FormLabel>{label}</FormLabel>
      <Select
        value={value}
        placeholder="Choose photo"
        onChange={(_, v) => set(v)}
      >
        {photos
          .filter((p) => p.image_url)
          .map((p) => (
            <Option value={p.id} key={p.id}>
              {name(p)}
            </Option>
          ))}
      </Select>
    </FormControl>
  );
  return (
    <Card sx={{ mb: 2 }}>
      <Typography level="title-lg">Before and after</Typography>
      {(error || command.error) && (
        <Alert color="danger">
          {command.error?.message || "Could not load photo comparisons."}
        </Alert>
      )}
      <Box
        sx={{ display: "flex", gap: 1, alignItems: "end", flexWrap: "wrap" }}
      >
        {select("Before photo", before, setBefore)}
        {select("After photo", after, setAfter)}
        <Button
          disabled={!before || !after || before === after}
          loading={command.isPending}
          onClick={() =>
            command.mutate({ action: "pair_photos", before, after })
          }
        >
          Save comparison
        </Button>
      </Box>
      {data?.photo_pairs.map((pair) => (
        <Box key={`${pair.before}-${pair.after}`}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 1,
            }}
          >
            {[pair.before, pair.after].map((id, index) => {
              const photo = photos.find((p) => p.id === id);
              return (
                photo && (
                  <Box key={id}>
                    <Typography level="title-sm">
                      {index ? "After" : "Before"} · {name(photo)}
                    </Typography>
                    <Box
                      component="img"
                      src={photo.image_url}
                      alt={`${index ? "After" : "Before"}: ${name(photo)}`}
                      sx={{
                        width: "100%",
                        height: 220,
                        objectFit: "contain",
                        bgcolor: "background.level1",
                      }}
                    />
                  </Box>
                )
              );
            })}
          </Box>
          <Button
            size="sm"
            variant="plain"
            color="neutral"
            disabled={command.isPending}
            onClick={() => command.mutate({ action: "remove_pair", ...pair })}
          >
            Remove comparison
          </Button>
        </Box>
      ))}
    </Card>
  );
}
export function DocumentVersions({
  jobId,
  documents,
}: {
  jobId: string;
  documents: any[];
}) {
  const { data, command, error } = useJobWorkspace(jobId);
  const [base, setBase] = useState<number | null>(null);
  const [replacement, setReplacement] = useState<number | null>(null);
  return (
    <Card sx={{ mb: 2 }}>
      <Typography level="title-lg">Document versions</Typography>
      <Typography level="body-sm">
        Upload a revision using Add Documents, then link it here. Previous files
        remain available.
      </Typography>
      {(error || command.error) && (
        <Alert color="danger">
          {command.error?.message || "Could not load document versions."}
        </Alert>
      )}
      <Box
        sx={{ display: "flex", gap: 1, alignItems: "end", flexWrap: "wrap" }}
      >
        {[
          ["Previous document", base, setBase],
          ["New revision", replacement, setReplacement],
        ].map(([label, value, set]) => (
          <FormControl key={String(label)} sx={{ flex: 1, minWidth: 150 }}>
            <FormLabel>{String(label)}</FormLabel>
            <Select
              value={value as number | null}
              placeholder="Choose document"
              onChange={(_, v) => (set as (id: number | null) => void)(v)}
            >
              {documents.map((doc) => (
                <Option key={doc.id} value={doc.id}>
                  {name(doc)}
                </Option>
              ))}
            </Select>
          </FormControl>
        ))}
        <Button
          disabled={!base || !replacement || base === replacement}
          loading={command.isPending}
          onClick={() =>
            command.mutate({ action: "link_version", base, replacement })
          }
        >
          Link revision
        </Button>
      </Box>
      <Stack spacing={1}>
        {data?.document_families.map((family) => (
          <Box
            key={family.id}
            sx={{ borderTop: "1px solid", borderColor: "divider", pt: 1 }}
          >
            {family.versions.map((id, index) => {
              const doc = documents.find((d) => d.id === id);
              return (
                doc && (
                  <Box
                    key={id}
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      flexWrap: "wrap",
                      my: 0.5,
                    }}
                  >
                    <Typography level="body-sm">
                      v{index + 1} · {name(doc)}
                    </Typography>
                    <Button
                      component="a"
                      href={doc.file_url || doc.file}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="plain"
                      size="sm"
                    >
                      Open
                    </Button>
                    <Button
                      size="sm"
                      color={family.current === id ? "success" : "neutral"}
                      variant="outlined"
                      disabled={family.current === id || command.isPending}
                      onClick={() =>
                        command.mutate({
                          action: "select_version",
                          document: id,
                        })
                      }
                    >
                      {family.current === id
                        ? "Current revision"
                        : "Make current"}
                    </Button>
                  </Box>
                )
              );
            })}
          </Box>
        ))}
      </Stack>
    </Card>
  );
}
